import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'

import { LocationIcon } from '../icons/LocationIcon'
import { BRANCH_DATA } from './config'

interface IBranchToggleProps {
  onToggleBranch: (coordinates: number[], branchId: string) => void
  filteredBranches: typeof BRANCH_DATA
  focusedBranch: string | null
}

export function BranchToggle({
  onToggleBranch,
  filteredBranches,
  focusedBranch
}: IBranchToggleProps) {
  const t = useTranslations('Branches')
  const containerRef = useRef<HTMLDivElement>(null)
  const branchRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useEffect(() => {
    if (
      focusedBranch &&
      branchRefs.current[focusedBranch] &&
      containerRef.current
    ) {
      const container = containerRef.current
      const element = branchRefs.current[focusedBranch]

      if (element) {
        const containerRect = container.getBoundingClientRect()
        const elementRect = element.getBoundingClientRect()

        // Calculate element position relative to container
        const elementTopRelativeToContainer =
          elementRect.top - containerRect.top + container.scrollTop

        // Scroll container to center the element
        container.scrollTo({
          top:
            elementTopRelativeToContainer -
            container.clientHeight / 2 +
            element.offsetHeight / 2,
          behavior: 'smooth'
        })
      }
    }
  }, [focusedBranch])

  return (
    <div className="w-full rounded-2xl border border-gray-100 bg-gray-50 p-2">
      <div
        className="flex max-h-[400px] w-full flex-col gap-2 overflow-y-auto py-1"
        ref={containerRef}
      >
        {filteredBranches.map((branch) => (
          <div
            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- needs div layout, not button reset styles
            role="button"
            tabIndex={0}
            key={branch.id}
            ref={(el) => {
              branchRefs.current[branch.id] = el
            }}
            className={cn(
              'group ring-primary-500 mx-auto w-[95%] cursor-pointer rounded-2xl p-4 text-left ring-offset-1 transition-all ease-in hover:ring-1 active:ring-2',
              focusedBranch === branch.id ? 'bg-primary-500' : 'bg-white'
            )}
            onClick={() => onToggleBranch(branch.cord, branch.id)}
          >
            <div
              className={cn(
                'mb-3 flex h-10 w-10 items-center justify-center rounded-xl',
                focusedBranch === branch.id ? 'bg-white' : 'bg-gray-50'
              )}
            >
              <LocationIcon className="text-primary-500" />
            </div>
            <h3
              className={cn(
                'text-bold text-body-2',
                focusedBranch === branch.id ? 'text-white' : 'text-primary-600'
              )}
            >
              {t(branch.city)}
            </h3>
            <p
              className={cn(
                'text-body-3',
                focusedBranch === branch.id ? 'text-white' : 'text-primary-400'
              )}
            >
              {t(branch.street)}
            </p>
            <hr
              className={cn(
                'my-2 border-[1px] text-gray-50',
                focusedBranch === branch.id && 'text-primary-400'
              )}
            />
            <div>
              <p
                className={cn(
                  'text-[14px]',
                  focusedBranch === branch.id
                    ? 'text-white'
                    : 'text-primary-400'
                )}
              >
                {t(branch.days.monFry)}: {t(branch.hours.monFry)},{' '}
                {t(branch.days.satSun)}: {t(branch.hours.satSun)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
